import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnInit, signal, OnDestroy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute } from '@angular/router';
import { AuthStore } from 'src/app/store/auth-store';
import { environment } from 'src/environment';
import { LoginService } from 'src/app/shared/services/login-service';
import { MatIconModule } from '@angular/material/icon';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { israeliIdValidator } from '../validators/israeli-id.validator';
import { otpValidator } from '../validators/otp.validator';

@Component({
    selector: 'que-id-otp-login',
    imports: [CommonModule, MatFormFieldModule, MatCardModule, ReactiveFormsModule, MatInputModule, MatIconModule, TranslateDirective, TranslatePipe, MatButtonModule],
    templateUrl: './id-otp-login.component.html',
    styleUrls: ['./id-otp-login.component.scss'],
    providers: [
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: { appearance: "fill", floatLabel: "always" }
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class IdOtpLoginComponent implements OnInit, OnDestroy {
  @Input() formControl?: FormControl;

  constructor(
    private route: ActivatedRoute,
  ) { }

  loadingLogin$ = toSignal(inject(AuthStore).loadingLogin$);
  otpSent$ = toSignal(inject(AuthStore).otpSent$);
  sendingOtp$ = toSignal(inject(AuthStore).sendingOtp$);
  otpError$ = toSignal(inject(AuthStore).otpError$);
  otpRemainingTime$ = toSignal(inject(AuthStore).otpRemainingTime$);

  ngOnInit() {
    const redirectTo = "redirectUrl";
    this.redirectTo = this.route.snapshot.queryParams[redirectTo] || "/";

    this.loginForm.valueChanges.subscribe((formValue) => {
      this.#authStore.resetError();//עושה בעיות

      if (this.formControl) {
        // שליחת הטופס המלא עם ID ו-OTP (אם קיים)
        const fullFormValue = {
          id: formValue.Id,
          otp: formValue.Otp
        };
        this.formControl.setValue(fullFormValue);
        this.formControl.markAsTouched();

        // הטופס תקין רק אם יש ID תקין ו-OTP מלא
        const isFormValid = this.loginForm.valid && this.otpSent$() && formValue.Otp;
        if (!isFormValid) {
          this.formControl.setErrors({ invalid: true });
        } else {
          this.formControl.setErrors(null);
        }
      }
    });
    this.loginForm.controls.Id.valueChanges.subscribe(() => {
      this.#authStore.resetOtpOnIdChange();
      this.loginForm.get('Otp')?.reset();
      this.loginForm.get('Otp')?.markAsUntouched();
    });
  }

  ngOnDestroy() {
    // Cleanup handled by AuthStore
  }

  // פונקציה להצגת זמן נותר בפורמט דקות:שניות
  getFormattedRemainingTime(): string {
    return this.#authStore.getFormattedRemainingTime();
  }

  #builder = inject(FormBuilder);
  #loginService = inject(LoginService);

  loginForm = this.#builder.group({
    Id: new FormControl<string | null>(null, [Validators.required, israeliIdValidator()]),
    Otp: new FormControl<string | null>(null, [Validators.required, otpValidator()]),
  });

  #authStore = inject(AuthStore);
  showPassword = false;
  redirectTo!: string;

  client = environment.client;
  title = environment.title;

  // בדיקה אם המחרוזת היא מפתח תרגום או הודעה ישירה מהשרת
  isTranslationKey(message: string): boolean {
    return message.startsWith('login.') || message.includes('errors.');
  }

  sendOtp() {
    const idValue = this.loginForm.get('Id')?.value;
    if (!idValue || this.loginForm.get('Id')?.invalid) {
      return;
    }

    // ניקוי שדה OTP
    this.loginForm.get('Otp')?.reset();
    this.loginForm.get('Otp')?.markAsUntouched();

    // שליחת הקוד דרך AuthStore
    this.#authStore.sendOtp(idValue);
  }
  onIdEnter(event: Event): void {
  event.preventDefault();

  if (this.loginForm.get('Id')?.invalid || this.sendingOtp$() || this.otpSent$()) {
    return;
  }

  this.sendOtp();
}
}